import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, Shield, Heart, Users, ArrowRight, Lock, Sparkles,
  CheckCircle, AlertTriangle, Building2, UserCheck, Calendar,
  Brain, Activity, MessageCircle, GraduationCap, Stethoscope
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageTransition } from '@/components/motion/Animations';

export function Tratamentos() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <PageTransition>
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-3">
          <BookOpen className="h-7 w-7 text-violet-600" strokeWidth={1.5} />
          <h1 className="text-2xl font-bold">{t('supportProgram.title')}</h1>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          {t('supportProgram.subtitle')}
        </p>
      </div>

      {/* Quick Access Cards */}
      <div className="grid md:grid-cols-2 gap-3 mb-8">
        <Card className="rounded-2xl border-border/40 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/curso/profissional')} data-testid="course-professional-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
              <GraduationCap className="h-6 w-6 text-violet-600 dark:text-violet-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">Formação PAREI-AD</h3>
              <p className="text-xs text-muted-foreground">Curso Profissional · 15 módulos</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/40 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/curso/familiar')} data-testid="course-family-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 bg-rose-100 dark:bg-rose-900/30 rounded-xl">
              <Heart className="h-6 w-6 text-rose-500 dark:text-rose-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">Programa Familiar</h3>
              <p className="text-xs text-muted-foreground">Plano de 90 Dias</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>
      </div>

      {/* O que é o Programa de Apoio U-Clean */}
      <Card className="mb-8 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Heart className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">{t('supportProgram.whatIsTitle')}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t('supportProgram.whatIsSubtitle')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-gray-700 leading-relaxed">
            {t('supportProgram.whatIsDescription')}
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-5 border shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold">{t('supportProgram.pillar1Title')}</h4>
              </div>
              <p className="text-sm text-gray-600">{t('supportProgram.pillar1Description')}</p>
            </div>
            
            <div className="bg-white rounded-lg p-5 border shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-green-600" />
                <h4 className="font-semibold">{t('supportProgram.pillar2Title')}</h4>
              </div>
              <p className="text-sm text-gray-600">{t('supportProgram.pillar2Description')}</p>
            </div>
            
            <div className="bg-white rounded-lg p-5 border shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                <h4 className="font-semibold">{t('supportProgram.pillar3Title')}</h4>
              </div>
              <p className="text-sm text-gray-600">{t('supportProgram.pillar3Description')}</p>
            </div>
          </div>

          <p className="text-gray-700 text-center font-medium bg-green-50 p-4 rounded-lg border border-green-200">
            {t('supportProgram.goalStatement')}
          </p>
        </CardContent>
      </Card>

      {/* Metodologia Enantiodromia */}
      <Card className="mb-8 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Sparkles className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">{t('supportProgram.methodologyTitle')}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t('supportProgram.methodologySubtitle')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-gray-700 leading-relaxed">
            {t('supportProgram.methodologyDescription')}
          </p>

          <div className="bg-white rounded-lg p-6 border">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              {t('supportProgram.methodologyHelpsTitle')}
            </h4>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <Activity className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.methodologyHelp1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.methodologyHelp2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <UserCheck className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.methodologyHelp3')}</span>
              </li>
            </ul>
          </div>

          {/* Importante - Não substitui tratamento */}
          <div className="bg-amber-50 rounded-lg p-5 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-amber-900 mb-2">{t('supportProgram.importantNote')}</h5>
                <p className="text-sm text-amber-800">
                  {t('supportProgram.importantNoteText')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* O que está incluído */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-600" />
            {t('supportProgram.includedTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included1')}</span>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included2')}</span>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included3')}</span>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included4')}</span>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included5')}</span>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{t('supportProgram.included6')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Para quem é indicado */}
      <Card className="mb-8 bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-6 w-6 text-blue-600" />
            {t('supportProgram.forWhomTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">{t('supportProgram.forWhomDescription')}</p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              {t('supportProgram.forWhom1')}
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              {t('supportProgram.forWhom2')}
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              {t('supportProgram.forWhom3')}
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              {t('supportProgram.forWhom4')}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Clínicas e Profissionais Parceiros */}
      <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-lg">
              <Building2 className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">{t('supportProgram.partnersTitle')}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t('supportProgram.partnersSubtitle')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-gray-700 leading-relaxed">
            {t('supportProgram.partnersDescription')}
          </p>

          <div className="bg-white rounded-lg p-6 border">
            <h4 className="font-semibold mb-4">{t('supportProgram.whenToSeekHelp')}</h4>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <Stethoscope className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.seekHelp1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Brain className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.seekHelp2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{t('supportProgram.seekHelp3')}</span>
              </li>
            </ul>
          </div>

          <p className="text-sm text-gray-600 italic">
            {t('supportProgram.partnersDisclaimer')}
          </p>
        </CardContent>
      </Card>

      {/* Comunidade Gratuita */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">{t('supportProgram.freeCommTitle')}</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {t('supportProgram.freeCommDescription')}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/grupos')}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <Users className="h-4 w-4 mr-2" />
                {t('supportProgram.exploreGroups')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CTA - Plano Fundador */}
      <Card className="mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300">
        <CardContent className="p-8 text-center">
          <Heart className="h-12 w-12 text-amber-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">{t('supportProgram.founderCTATitle')}</h3>
          <p className="text-gray-700 mb-6 max-w-xl mx-auto">
            {t('supportProgram.founderCTADescription')}
          </p>
          <Button
            onClick={() => navigate('/checkout')}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold"
          >
            {t('supportProgram.founderCTAButton')}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* DISCLAIMER LEGAL OBRIGATÓRIO */}
      <Card className="border-2 border-red-300 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-lg mb-3 text-red-800">
                ⚠️ {t('supportProgram.disclaimerTitle')}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                {t('supportProgram.disclaimerText')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageTransition>
  );
}
